import React from "react";

import { getSafe } from "@jslib/shared/utils/getSafe";
import { logger } from "@jslib/shared/utils/logger";

import {
  type AllStatuses,
  type CollectFieldsResponse,
  type FieldDescription,
  type VerificationStep,
  type ViewModel,
  type CollectFieldsAction,
} from "../types/types";
import { useAppDispatch } from "../state/contextHooks";

import { getFieldsToCollect } from "./VerificationApiUtils";

export const useCollectFieldsEndpoint = <VM extends ViewModel = ViewModel>({
  verificationId,
  currentStep,
  viewModel,
  defaultRequiredFields = [],
}: {
  verificationId: string;
  currentStep: VerificationStep;
  viewModel: VM;
  defaultRequiredFields?: FieldDescription[];
}) => {
  const dispatch = useAppDispatch();
  const status = "status" in viewModel ? viewModel.status : undefined;

  const [requiredFields, setRequiredFields] =
    React.useState<FieldDescription[]>(defaultRequiredFields);
  const [optionalFields, setOptionalFields] = React.useState<FieldDescription[]>([]);
  const [availableStatuses, setAvailableStatuses] = React.useState<AllStatuses[] | undefined>(
    undefined,
  );

  const isFieldRequired = (key: keyof VM) => requiredFields.some((field) => field.key === key);
  const isFieldOptional = (key: keyof VM) => optionalFields.some((field) => field.key === key);

  React.useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      try {
        const collectFieldsResponse: CollectFieldsResponse = await getFieldsToCollect(
          verificationId,
          currentStep,
          viewModel,
        );
        const newRequiredFields = getSafe(() => collectFieldsResponse.fieldsToCollect.required, []);
        const newOptionalFields = getSafe(() => collectFieldsResponse.fieldsToCollect.optional, []);
        if (!abortController.signal.aborted) {
          setRequiredFields(newRequiredFields);
          setOptionalFields(newOptionalFields);
          // Preserve reference identity when statuses haven't changed to avoid
          // unnecessary re-renders in consuming components.
          setAvailableStatuses((prev) => {
            const next = collectFieldsResponse.availableStatuses;
            if (
              prev &&
              next &&
              prev.length === next.length &&
              prev.every((s, i) => s === next[i])
            ) {
              return prev;
            }
            return next;
          });

          // Dispatch the field requirements to the reducer
          const collectFieldsAction: CollectFieldsAction = {
            type: "COLLECT_FIELDS",
            requiredFields: newRequiredFields,
            optionalFields: newOptionalFields,
          };
          dispatch(collectFieldsAction);
        }
      } catch (error) {
        logger.error(`Failed to determine fields to collect: ${error}`, currentStep);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [viewModel.countryChoice?.value, status]);

  return { isFieldRequired, isFieldOptional, availableStatuses };
};
